import * as z from 'zod';

// Donations of this amount or above must collect the donor's tax identification
// data (document + address) for regulatory compliance, regardless of whether the
// donor requested a tax certificate.
export const FISCAL_DATA_THRESHOLD = 100;

export const requiresFiscalData = ({
	portalId,
	certificate,
	amount,
}: {
	portalId: string;
	certificate: boolean;
	amount: number | undefined;
}) => certificate || portalId === 'telefonica' || (amount ?? 0) >= FISCAL_DATA_THRESHOLD;

export const detailsSchema = (portalId: string, amount: number | undefined) =>
	z
		.object({
			firstname: z.string().min(1),
			lastname: z.string().min(1),
			email: z.email(),
			repeatEmail: z.email(),
			certificate: z.boolean(),
			documentType: z.enum(['id', 'passport', 'taxId', 'nie', 'others']),
			documentNumber: z.string(),
			streetName: z.string(),
			streetNumber: z.string(),
			additionalInfo: z.string().or(z.undefined()),
			city: z.string(),
			cp: z.string(),
			country: z.string(),
			region: z.string(),
			province: z.string(),
			termsAndConditions: z.boolean().refine((val) => val, { params: { i18n: 'termsAndConditions' } }),
			ngosTermsAndConditions: z.boolean(),
			telefonicaTermsAndConditions: z.boolean(),
			newsletter: z.boolean(),
		})
		.check((ctx) => {
			const fiscalRequired = requiresFiscalData({ portalId, certificate: ctx.value.certificate, amount });

			if (ctx.value.repeatEmail !== ctx.value.email) {
				ctx.issues.push({
					code: 'custom',
					params: { i18n: 'emailsDoNotMatch' },
					path: ['repeatEmail'],
					input: ctx.value,
				});
			}

			if (!ctx.value.documentNumber && fiscalRequired) {
				ctx.issues.push({
					code: 'custom',
					params: { i18n: 'required' },
					path: ['documentNumber'],
					input: ctx.value,
				});
			}

			if (!ctx.value.streetName && fiscalRequired) {
				ctx.issues.push({
					code: 'custom',
					params: { i18n: 'required' },
					path: ['streetName'],
					input: ctx.value,
				});
			}

			if (!ctx.value.streetNumber && fiscalRequired) {
				ctx.issues.push({
					code: 'custom',
					params: { i18n: 'required' },
					path: ['streetNumber'],
					input: ctx.value,
				});
			}

			if (!ctx.value.city && fiscalRequired) {
				ctx.issues.push({
					code: 'custom',
					params: { i18n: 'required' },
					path: ['city'],
					input: ctx.value,
				});
			}

			if (!ctx.value.cp && fiscalRequired) {
				ctx.issues.push({
					code: 'custom',
					params: { i18n: 'required' },
					path: ['cp'],
					input: ctx.value,
				});
			}

			if (!ctx.value.country && fiscalRequired) {
				ctx.issues.push({
					code: 'custom',
					params: { i18n: 'required' },
					path: ['country'],
					input: ctx.value,
				});
			}

			if (!ctx.value.region && fiscalRequired) {
				ctx.issues.push({
					code: 'custom',
					params: { i18n: 'required' },
					path: ['region'],
					input: ctx.value,
				});
			}

			if (!ctx.value.province && fiscalRequired) {
				ctx.issues.push({
					code: 'custom',
					params: { i18n: 'required' },
					path: ['province'],
					input: ctx.value,
				});
			}

			if (ctx.value.country !== 'AD' && ctx.value.region === '' && fiscalRequired) {
				ctx.issues.push({
					code: 'custom',
					params: { i18n: 'required' },
					path: ['region'],
					input: ctx.value,
				});
			}

			if (ctx.value.country !== 'AD' && ctx.value.province === '' && fiscalRequired) {
				ctx.issues.push({
					code: 'custom',
					params: { i18n: 'required' },
					path: ['province'],
					input: ctx.value,
				});
			}

			if (!ctx.value.telefonicaTermsAndConditions && portalId === 'telefonica') {
				ctx.issues.push({
					code: 'custom',
					params: { i18n: 'termsAndConditions' },
					path: ['telefonicaTermsAndConditions'],
					input: ctx.value,
				});
			}

			if (!ctx.value.ngosTermsAndConditions && ctx.value.certificate) {
				ctx.issues.push({
					code: 'custom',
					params: { i18n: 'termsAndConditions' },
					path: ['ngosTermsAndConditions'],
					input: ctx.value,
				});
			}
		});

export type DetailsSchema = ReturnType<typeof detailsSchema>;
export type Details = z.infer<DetailsSchema>;
